import {
  InventoryPermission,
  Module,
  StockInPermission,
  StockOutPermission,
  type ModuleId,
  type PermissionId,
} from "@mh-wms/domain";

/** Claims emitted by the Go API. They are UI hints; the server enforces RBAC. */
export interface TokenClaims {
  modules: number[];
  permissions: string[];
  role: string;
  email: string;
  userId: string;
  /** Expiry as epoch seconds. */
  exp: number;
}

const CLAIM_NAME_ID =
  "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier";
const CLAIM_EMAIL =
  "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress";

export type Base64Decoder = (encoded: string) => string;

/**
 * Decode an unverified JWT payload for client-side presentation decisions.
 * Base64 decoding is injected because browsers and native runtimes expose
 * different implementations.
 */
export function decodeToken(
  token: string,
  decodeBase64: Base64Decoder,
): TokenClaims | null {
  try {
    const payload = token.split(".")[1];
    if (!payload) return null;
    const padded = payload
      .replace(/-/g, "+")
      .replace(/_/g, "/")
      .padEnd(Math.ceil(payload.length / 4) * 4, "=");
    const claims = JSON.parse(decodeBase64(padded)) as Record<string, unknown>;
    return {
      modules: parseModules(claims.Modules),
      permissions: parsePermissions(claims.Modules),
      role: typeof claims.Role === "string" ? claims.Role : "",
      email: typeof claims[CLAIM_EMAIL] === "string" ? claims[CLAIM_EMAIL] : "",
      userId:
        typeof claims[CLAIM_NAME_ID] === "string" ? claims[CLAIM_NAME_ID] : "",
      exp: typeof claims.exp === "number" ? claims.exp : 0,
    };
  } catch {
    return null;
  }
}

function parsePermissions(raw: unknown): string[] {
  if (typeof raw !== "string") return [];
  const known = new Set<string>([
    ...Object.values(InventoryPermission),
    ...Object.values(StockInPermission),
    ...Object.values(StockOutPermission),
  ]);
  return raw
    .split(",")
    .map((part) => part.trim().toLowerCase())
    .filter((part) => known.has(part));
}

function parseModules(raw: unknown): number[] {
  if (typeof raw !== "string" || !raw.trim()) return [];
  return raw
    .split(",")
    .map((part) => {
      const value = part.trim();
      if (/^-?\d+$/.test(value)) return Number(value);
      return MODULE_NAMES[value.toUpperCase()];
    })
    .filter((value): value is ModuleId => Number.isInteger(value));
}

const MODULE_NAMES = Object.fromEntries(
  Object.entries(Module).map(([name, value]) => [name.toUpperCase(), value]),
) as Record<string, ModuleId | undefined>;

export function isExpired(
  claims: TokenClaims | null,
  now = Date.now(),
): boolean {
  return !claims?.exp || now >= claims.exp * 1000;
}

/** Cosmetic module visibility only; backend authorization remains authoritative. */
export function canSeeModule(
  moduleId: ModuleId,
  claims: Pick<TokenClaims, "modules" | "role"> | null,
): boolean {
  if (!claims) return false;
  const role = claims.role.trim().toLowerCase();
  if (
    ["admin", "administrator", "superadmin", "superadministrator"].includes(
      role.replaceAll(" ", ""),
    )
  )
    return true;
  return claims.modules.includes(moduleId);
}

/** Fine-grained UI visibility with legacy Inventory roles treated as full access. */
export function canSeePermission(
  permission: PermissionId,
  claims: Pick<TokenClaims, "modules" | "permissions" | "role"> | null,
): boolean {
  if (!claims) return false;
  const role = claims.role.trim().toLowerCase().replaceAll(" ", "");
  if (
    permission !== StockInPermission.Export &&
    permission !== StockOutPermission.Export &&
    ["admin", "administrator", "superadmin", "superadministrator"].includes(
      role,
    )
  ) {
    return true;
  }
  if (
    permission === StockInPermission.Export ||
    permission === StockOutPermission.Export
  ) {
    return (
      canSeeModule(
        permission === StockInPermission.Export
          ? Module.StockIn
          : Module.StockOut,
        claims,
      ) && claims.permissions.includes(permission)
    );
  }
  if (permission === StockInPermission.BulkUpload) {
    return (
      canSeeModule(Module.StockIn, claims) &&
      claims.permissions.includes(permission)
    );
  }
  if (!canSeeModule(Module.Inventory, claims)) return false;
  const inventoryPermissions = claims.permissions.filter((value) =>
    (Object.values(InventoryPermission) as string[]).includes(value),
  );
  return (
    inventoryPermissions.length === 0 ||
    inventoryPermissions.includes(permission)
  );
}
